import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Introduction,
  Job,
  JobCompanyInfo,
} from '@kanda-libs/ks-frontend-services';

import { URLS } from 'config';
import { selectors } from 'store';
import type { FormattedJob } from 'types';

interface CurrentJobHook {
  id: string | undefined;
  job: FormattedJob;
  jobInfo?: Job;
  jobCompanyInfo?: JobCompanyInfo;
  introduction?: Introduction;
  isLoading: boolean;
  isSolar: boolean;
  isExampleJob: boolean;
  isSubmitting: boolean;
  isIntroduced: boolean;
}

/**
 * Returns current job and loading state
 */
const useCurrentJob = (): CurrentJobHook => {
  const pathKey = useSelector(selectors.getPathKey);
  const { push } = useHistory();

  const id = pathKey.id as string | undefined;

  const isSubmitting = useSelector(selectors.job.getIsSubmitting);
  const jobInfo = useSelector(selectors.job.getEntity);
  const jobHasFetched = useSelector(selectors.job.getHasFetched);
  const jobCompanyInfo = useSelector(selectors.jobCompanyInfo.getEntity);
  const jobCompanyInfoHasFetched = useSelector(
    selectors.jobCompanyInfo.getHasFetched,
  );
  const introduction = useSelector(selectors.introduction.getEntity);

  const formattedJobInfo = jobInfo as FormattedJob;

  const job = useMemo(() => {
    if (!formattedJobInfo || !jobCompanyInfo) return null;
    return {
      ...formattedJobInfo,
      company: jobCompanyInfo,
    };
  }, [formattedJobInfo, jobCompanyInfo]);

  const isIntroduced = useMemo(() => {
    if (!job) return false;
    return Boolean(job?.introduced_to);
  }, [job]);

  const isSolar = useMemo(() => {
    if (!job) return false;
    return job?.job_type === 'solar';
  }, [job]);

  const isLoading =
    !jobHasFetched || !jobCompanyInfoHasFetched || formattedJobInfo?.id !== id;

  const isExampleJob = job?.flow_type === 'example';

  useEffect(() => {
    if (id) return;
    push(URLS.home);
  }, [push, id]);

  return {
    id,
    job: job as FormattedJob,
    jobInfo,
    jobCompanyInfo,
    introduction,
    isLoading,
    isSolar,
    isExampleJob,
    isSubmitting,
    isIntroduced,
  };
};

export default useCurrentJob;
